import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type User = { 
  id: string; 
  name: string; 
  email: string;
  role: string; 
  isVerified: boolean;
  token?: string;
  phoneNumber?: string;
};

type AuthState = {
  user: User | null;
  isAdmin: boolean;
};

// Admin email whitelist (HARD RULE)
const ADMIN_EMAILS = [
  'alimagdi12367@gmail.com',
  'youssefabdelmaged50@gmail.com',
  'admin@teo-eg.com',
] as const;

// Check if email is in admin whitelist
const isAdminEmail = (email: string): boolean => {
  return ADMIN_EMAILS.includes(email as typeof ADMIN_EMAILS[number]);
};

// Load user from localStorage on initialization
const loadUserFromStorage = (): User | null => {
  try {
    const storedUser = localStorage.getItem('auth_user');
    const token = localStorage.getItem('auth_token');
    if (storedUser && token) {
      const user = JSON.parse(storedUser);
      // Re-validate admin access or role on load
      if (user.role === 'admin' || (user.email && isAdminEmail(user.email))) {
        // Update token in case it changed
        user.token = token;
        return user;
      }
      // User not authorized for dashboard, clear storage
      localStorage.removeItem('auth_user');
      localStorage.removeItem('auth_token');
    }
  } catch (error) {
    console.error('Failed to load user from localStorage:', error);
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_token');
  }
  return null;
};

const loadedUser = loadUserFromStorage();

const initialState: AuthState = {
  user: loadedUser,
  isAdmin: loadedUser ? (loadedUser.role === 'admin' || isAdminEmail(loadedUser.email)) : false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      // Validate admin access before setting user (either by role or by whitelist)
      const isAuthorized = action.payload.role === 'admin' || isAdminEmail(action.payload.email);
      
      if (!isAuthorized) {
        state.user = null;
        state.isAdmin = false;
        localStorage.removeItem('auth_user');
        localStorage.removeItem('auth_token');
        return;
      }
      
      state.user = action.payload;
      state.isAdmin = true;
      
      // Save to localStorage
      try {
        localStorage.setItem('auth_user', JSON.stringify(action.payload));
        if (action.payload.token) {
          localStorage.setItem('auth_token', action.payload.token);
        }
      } catch (error) {
        console.error('Failed to save user to localStorage:', error);
      }
    },
    logout: (state) => {
      state.user = null;
      state.isAdmin = false;
      // Remove from localStorage
      try {
        localStorage.removeItem('auth_user');
        localStorage.removeItem('auth_token');
      } catch (error) {
        console.error('Failed to remove user from localStorage:', error);
      }
    },
  },
});

export const { setUser, logout } = authSlice.actions;
export default authSlice.reducer;

